/**
 * ExchangeRate Service
 * Servicio para obtener tipos de cambio
 */

import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GraphService } from 'src/app/shared/lib/api/graph.service';
import {
  GET_LATEST_EXCHANGE_RATE,
  GET_EXCHANGE_RATE_BY_DATE,
  GET_MONTHLY_EXCHANGE_RATES_BY_YEAR,
} from '../graphql/exchange-rate.queries';
import { ExchangeRate } from '../models';
import { SAVE_EXCHANGE_RATE } from '../graphql/exchange-rate.mutation';

@Injectable({ providedIn: 'root' })
export class ExchangeRateService {
  constructor(private graphService: GraphService) { }

  /**
   * Obtener el último tipo de cambio
   * Usado en nuevo-rc y editar-rc para calcular conversiones
   */
  getLatest(): Observable<{ latestExchangeRate: ExchangeRate | null }> {
    return this.graphService
      .queryGraphQL<{ latestExchangeRate: ExchangeRate | null }>({
        query: GET_LATEST_EXCHANGE_RATE,
        variables: {},
      })
      .pipe(
        catchError(error => {
          console.error('Error fetching latest exchange rate:', error);
          return throwError(() => new Error('Error al obtener tipo de cambio'));
        }),
      );
  }

  /**
   * Obtener tipo de cambio por fecha
   * @param exchangeDate Fecha en formato YYYY-MM-DD
   */
  getByDate(exchangeDate: string): Observable<{ exchangeRateByDate: ExchangeRate | null }> {
    return this.graphService
      .queryGraphQL<{ exchangeRateByDate: ExchangeRate | null }>({
        query: GET_EXCHANGE_RATE_BY_DATE,
        variables: { exchangeDate },
      })
      .pipe(
        catchError(error => {
          console.error(`Error fetching exchange rate for ${exchangeDate}:`, error);
          return throwError(() => new Error('Error al obtener tipo de cambio'));
        }),
      );
  }

  /**
   * Obtener tipos de cambio mensuales para un año específico y mes específico
   * @param month Numero del mes (1-12)
   * @param year Numero del año (e.g., 2024)
   */
  getMonthlyByYear(month: number, year: number): Observable<{ exchangeRatesByMonthYear: ExchangeRate[] }> {
    return this.graphService
      .queryGraphQL<{ exchangeRatesByMonthYear: ExchangeRate[] }>({
        query: GET_MONTHLY_EXCHANGE_RATES_BY_YEAR,
        variables: { year, month },
      })
      .pipe(
        catchError(error => {
          console.error(`Error fetching monthly exchange rates for year ${year} and month ${month}:`, error);
          return throwError(() => new Error('Error al obtener tipos de cambio mensuales'));
        }),
      );
  }

  /**
   * Método para crear o actualizar un tipo de cambio
   * @param exchangeRate Datos del tipo de cambio a crear o actualizar
   */
  save(exchangeRate: Partial<ExchangeRate>): Observable<any> {
    return this.graphService
      .mutateGraphQL({
        mutation: SAVE_EXCHANGE_RATE,
        variables: { exchangeRate },
      })
      .pipe(
        catchError(error => {
          console.error('Error saving exchange rate:', error);
          return throwError(() => new Error('Error al guardar tipo de cambio'));
        }),
      );
  }
}
